import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/ui/SectionHeading'

interface TreatmentListSectionProps {
  title?: string
  subtitle?: string
  items: string[]
  variant?: 'check' | 'bullet'
  centered?: boolean
  background?: 'white' | 'cream' | 'soft'
  className?: string
}

export function TreatmentListSection({
  title,
  subtitle,
  items,
  variant = 'bullet',
  centered = false,
  background = 'white',
  className,
}: TreatmentListSectionProps) {
  if (!items || items.length === 0) return null

  const bgClass = {
    white: 'bg-white',
    cream: 'bg-cream',
    soft: 'bg-primary-soft',
  }[background]

  const cardClass = background === 'white' ? 'bg-cream' : 'bg-white'

  return (
    <section className={cn('py-16 md:py-24', bgClass, className)}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          title={title || ''}
          subtitle={subtitle}
          centered={centered}
        />

        <ul
          className={cn(
            'grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6',
            title && 'mt-10 md:mt-12'
          )}
        >
          {items.map((item, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className={cn('flex items-start gap-4 p-4 rounded-xl', cardClass)}
            >
              {variant === 'check' && (
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
              )}
              {variant === 'bullet' && (
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              )}
              <span className="text-sm md:text-base leading-relaxed text-muted">
                {item}
              </span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  )
}
